import { setup, assign, MachineContext } from "xstate";
import { createActorContext } from "@xstate/react";
import { createBrowserInspector } from "@statelyai/inspect";
import { fromPromise } from "xstate";
import ApiOrders, { OrderResponse } from "../../../api/orders";

interface OrderContext extends MachineContext {
  orderType: Order | null;
  orderTypeId: number | null;
  timezone: string;
  center: string[] | null;
  zoom: number | null;
  longitude: string;
  startTime: string | null;
  endTime: string | null;
  description: string;
  error: string | null;
  surveyId: number;
  orderId: number;
  scheduleAt: string | null;
  lockDetails: boolean;
  customDetails: boolean;
  autoSubmit: boolean;
}

type Order = "basic" | "inventory" | "custom" | "thermal" | "";
type OrderEvent =
  | { type: "SELECT_TYPE"; orderTypeId: number }
  | { type: "UPDATE_TYPE"; orderTypeId: number }
  | { type: "NEXT" }
  | { type: "RESET" }
  | { type: "GOTO_IMAGES" }
  | { type: "GOTO_GEOMETRY" }
  | { type: "GOTO_REVIEW" }
  | { type: "SET_TIMEZONE"; timezone: string }
  | { type: "SET_CENTER"; latitude?: string; longitude?: string }
  | { type: "SET_ZOOM"; zoom: number }
  | { type: "SET_START_TIME"; startTime: string | null }
  | { type: "SET_END_TIME"; endTime: string | null }
  | { type: "SET_DESCRIPTION"; description: string }
  | { type: "TOGGLE_DETAILS_LOCK" }
  | { type: "SET_CUSTOM_DETAILS"; customDetails: boolean }
  | { type: "TOGGLE_AUTO_SUBMIT" };

export const createOrderMachine = setup({
  types: {} as {
    context: OrderContext;
    events: OrderEvent;
    input: { surveyId: number };
  },
  actions: {
    navigate: () => {},
  },
  actors: {
    getTimezone: fromPromise(
      ({ input }: { input: { longitude: string; latitude: string } }) => {
        return fetch(`/api/v1/timezone/${input.longitude}/${input.latitude}`)
          .then((response) => response.json())
          .then((zone) => {
            return zone.timezone;
          });
      }
    ),
    submitOrder: fromPromise(
      ({
        input,
      }: {
        input: {
          timezone: string;
          center: string[];
          startTime: string;
          endTime: string;
          description: string;
        };
      }) => {
        return Promise.resolve();
      }
    ),
    createOrder: fromPromise(
      async ({
        input,
      }: {
        input: { surveyId: number; orderTypeId: number };
      }) => {
        return ApiOrders.createOrder(input.surveyId, {
          organization_order_type_id: input.orderTypeId,
        });
      }
    ),
    getActiveOrder: fromPromise(
      async ({ input }: { input: { surveyId: number } }) => {
        return ApiOrders.getOrders(input.surveyId).then((orders) =>
          orders
            .sort((a, b) => a.id - b.id)
            .find((order: OrderResponse) => order.submitted === null)
        );
      }
    ),
    updateOrderType: fromPromise(
      async ({
        input,
      }: {
        input: { surveyId: number; orderId: number; orderTypeId: number };
      }) => {
        return ApiOrders.updateOrderType(
          input.surveyId,
          input.orderId,
          input.orderTypeId
        );
      }
    ),
  },
}).createMachine({
  id: "manageOrder",
  initial: "check",
  context: ({ input }) =>
    ({
      orderType: null,
      orderTypeId: null,
      timezone: null,
      zoom: null,
      center: null,
      startTime: null,
      endTime: null,
      description: "",
      error: null,
      surveyId: input?.surveyId ?? 0,
      orderId: 0,
      scheduleAt: null,
      lockDetails: true,
      customDetails: false,
      autoSubmit: true,
    } as OrderContext),
  states: {
    close: {
      type: "final",
    },
    check: {
      initial: "loading",
      states: {
        loading: {
          invoke: {
            id: "getActiveOrder",
            src: "getActiveOrder",
            input: ({ context }) => ({
              surveyId: context.surveyId,
            }),
            onDone: [
              {
                guard: ({ event }) => event.output === undefined,
                target: "#manageOrder.create",
              },
              {
                target: "#manageOrder.create",
                actions: assign({
                  orderType: ({ event }) => event.output.type,
                  orderTypeId: ({ event }) => {
                    return event.output.organization_order_type_id;
                  },
                  orderId: ({ event }) => event.output.id,
                  scheduleAt: ({ event }) => event.output.scheduled_at,
                }),
              },
            ],
          },
        },
      },
    },
    create: {
      id: "create",
      initial: "idle",
      states: {
        idle: {
          on: {
            SET_CENTER: {
              actions: assign({
                center: ({ event, context }) => [
                  event.longitude ?? context.center?.[0] ?? "0",
                  event.latitude ?? context.center?.[1] ?? "0",
                ],
              }),
              target: "timezone",
            },
          },
        },
        timezone: {
          invoke: {
            id: "getTimezone",
            src: "getTimezone",
            input: ({ context }) => {
              return {
                longitude: context.center?.[0],
                latitude: context.center?.[1],
              };
            },
            onDone: {
              target: "idle",
              actions: assign({
                timezone: ({ event }) => event.output,
              }),
            },
          },
        },
        creating: {
          invoke: {
            id: "createOrder",
            src: "createOrder",
            input: ({ context, event }) => {
              if (event.type === "SELECT_TYPE") {
                return {
                  surveyId: context.surveyId,
                  orderTypeId: event.orderTypeId,
                };
              }

              throw new Error("Invalid event type");
            },
            onDone: {
              target: "idle",
              actions: assign({
                orderType: ({ event }) => event.output.order.type,
                orderTypeId: ({ event }) => event.output.order.organization_order_type_id,
                orderId: ({ event }) => event.output.order.id,
              }),
            },
            onError: {
              target: "idle",
              actions: assign({
                error: ({ event }) =>
                  (event.error as Error)?.message || String(event.error),
              }),
            },
          },
        },
        updatingType: {
          invoke: {
            id: "updateOrderType",
            src: "updateOrderType",
            input: ({ context, event }) => {
              if (event.type === "UPDATE_TYPE") {
                return {
                  surveyId: context.surveyId,
                  orderId: context.orderId,
                  orderTypeId: event.orderTypeId,
                };
              }
              throw new Error("Invalid event type");
            },
            onDone: {
              target: "idle",
              actions: assign({
                orderTypeId: ({ event }) => event.output.order.organization_order_type_id,
              }),
            },
            onError: {
              target: "idle",
              actions: assign({
                error: ({ event }) =>
                  (event.error as Error)?.message || String(event.error),
              }),
            },
          },
        },
        submitting: {
          invoke: {
            id: "submitOrder",
            src: "submitOrder",
            input: ({ context }) => ({
              center: context.center,
              timezone: context.timezone,
              startTime: context.startTime ?? null,
              endTime: context.endTime ?? null,
              description: context.description,
            }),
          },
          onDone: {
            target: "#manageOrder.close",
          },
        },
      },
      on: {
        SELECT_TYPE: {
          target: "create.creating",
        },
        UPDATE_TYPE: {
          actions: ["navigate"],
          target: "create.updatingType",
        },
        SET_TIMEZONE: {
          actions: assign({
            timezone: ({ event }) => event.timezone,
          }),
        },
        SET_ZOOM: {
          actions: assign({
            zoom: ({ event }) => event.zoom,
          }),
        },
        SET_CENTER: {
          actions: assign({
            center: ({ event, context }) => [
              event.longitude ?? context.center?.[0] ?? "0",
              event.latitude ?? context.center?.[1] ?? "0",
            ],
          }),
        },
        SET_START_TIME: {
          actions: assign({
            startTime: ({ event }) => {
              return event.startTime;
            },
            lockDetails: ({ context, event }) => {
              if (context.startTime === null && event.startTime === null) {
                return false;
              }

              return context.lockDetails;
            },
          }),
        },
        SET_END_TIME: {
          actions: assign({
            endTime: ({ event }) => event.endTime,
            lockDetails: ({ context, event }) => {
              if (context.endTime === null && event.endTime === null) {
                return false;
              }

              return context.lockDetails;
            },
          }),
        },
        SET_DESCRIPTION: {
          actions: assign({
            description: ({ event }) => event.description,
          }),
        },
        TOGGLE_DETAILS_LOCK: {
          actions: assign({
            lockDetails: ({ context }) => !context.lockDetails,
          }),
        },
        SET_CUSTOM_DETAILS: {
          actions: assign({
            customDetails: ({ event }) => event.customDetails,
          }),
        },
        TOGGLE_AUTO_SUBMIT: {
          actions: assign({
            autoSubmit: ({ context }) => !context.autoSubmit,
          }),
        },
        RESET: {
          actions: assign({
            orderType: null,
            timezone: null,
            center: null,
            startTime: null,
            endTime: null,
            description: "",
            error: null,
            scheduleAt: null,
            lockDetails: true,
            customDetails: false,
            autoSubmit: true,
          }),
        },
      },
    },
  },
});

export const OrderMachineContext = createActorContext(createOrderMachine, {});
